/* eslint-disable no-unused-vars */
// LQC-2254 - shared substring search over a list of reference-data objects. One
// implementation serves both the portfolio store and the bond security store (and
// anything else that needs a type-to-find list), so ranking behaves identically
// wherever a user searches.

// Lower is better, so a rank sorts ascending. NoMatch is filtered out, never sorted.
// AllTokens ranks last because the typed words matched in pieces, not as one run.
export enum SearchMatchRank {
  Exact = 0,
  StartsWith = 1,
  Contains = 2,
  AllTokens = 3,
  NoMatch = 99,
}

// Default cap on results. A search box only ever shows a handful, and the bond list
// is large enough that rendering all of it would be pointless.
export const DEFAULT_SEARCH_LIMIT = 50;

export interface ISearchItemsParams<T> {
  items: T[];
  term: string;
  fields: string[];
  limit?: number;
}

// items is capped at the limit; totalMatches is how many there were before capping, so
// a dropdown can say "50 of 312" instead of pretending 50 is all of them.
export interface ISearchResult<T> {
  items: T[];
  totalMatches: number;
}

// Trim + lower-case so every comparison in here is case and whitespace insensitive.
export function normaliseSearchTerm(value: unknown): string {
  if (value === null || value === undefined) return "";
  return String(value).trim().toLowerCase();
}

// How well one field value matches an already-normalised term.
export function returnSearchMatchRank(
  fieldValue: unknown,
  normalisedTerm: string,
): SearchMatchRank {
  const value = normaliseSearchTerm(fieldValue);
  if (!value || !normalisedTerm) return SearchMatchRank.NoMatch;
  if (value === normalisedTerm) return SearchMatchRank.Exact;
  if (value.startsWith(normalisedTerm)) return SearchMatchRank.StartsWith;
  if (value.includes(normalisedTerm)) return SearchMatchRank.Contains;
  return SearchMatchRank.NoMatch;
}

// Best rank across the searched fields. Earlier fields win ties, which is why the
// *_SEARCH_FIELDS arrays are ordered most-identifying first.
//
// Falls back to token matching when no single field contains the whole term: users
// type words in their own order ("UST 4.75", "RAGB 62"), and a plain substring test
// misses those against "UST 15May47 4.75%".
export function returnBestMatchRank<T>(
  item: T,
  fields: string[],
  normalisedTerm: string,
): SearchMatchRank {
  if (!normalisedTerm) return SearchMatchRank.NoMatch;

  let best = SearchMatchRank.NoMatch;
  for (const field of fields) {
    const rank = returnSearchMatchRank((item as any)?.[field], normalisedTerm);
    if (rank < best) best = rank;
    if (best === SearchMatchRank.Exact) return best;
  }
  if (best !== SearchMatchRank.NoMatch) return best;

  const tokens = normalisedTerm.split(/\s+/).filter(Boolean);
  if (tokens.length < 2) return SearchMatchRank.NoMatch;

  const values = fields.map((field) =>
    normaliseSearchTerm((item as any)?.[field]),
  );
  // Every token must land somewhere - any one missing rules the item out.
  const everyTokenFound = tokens.every((token) =>
    values.some((value) => value.includes(token)),
  );
  return everyTokenFound ? SearchMatchRank.AllTokens : SearchMatchRank.NoMatch;
}

// Ranked substring search. An empty term returns the head of the list unchanged,
// which is what a dropdown wants before the user has typed anything.
//
// totalMatches counts everything that matched BEFORE the limit was applied, so a caller
// can tell the user their list is cut short rather than truncating it silently.
export function searchItemsByFields<T>({
  items,
  term,
  fields,
  limit = DEFAULT_SEARCH_LIMIT,
}: ISearchItemsParams<T>): ISearchResult<T> {
  if (!Array.isArray(items) || items.length === 0) {
    return { items: [], totalMatches: 0 };
  }
  const normalisedTerm = normaliseSearchTerm(term);
  if (!normalisedTerm) {
    return { items: items.slice(0, limit), totalMatches: items.length };
  }

  const ranked = items
    .map((item, index) => ({
      item,
      index,
      rank: returnBestMatchRank(item, fields, normalisedTerm),
    }))
    .filter((entry) => entry.rank !== SearchMatchRank.NoMatch);

  // Index is the tiebreak so equally-ranked results keep the payload's order.
  ranked.sort((a, b) => a.rank - b.rank || a.index - b.index);
  return {
    items: ranked.slice(0, limit).map((entry) => entry.item),
    totalMatches: ranked.length,
  };
}

// Exact (case-insensitive) lookup on one field - the validation path that confirms a
// user's choice really exists in our data before it is sent to the back end.
export function findItemByFieldValue<T>(
  items: T[],
  field: string,
  value: unknown,
): T | undefined {
  const normalisedValue = normaliseSearchTerm(value);
  if (!Array.isArray(items) || !normalisedValue) return undefined;
  return items.find(
    (item) => normaliseSearchTerm((item as any)?.[field]) === normalisedValue,
  );
}
